import {usePitchDetectionStatus} from "../../hooks/pitchDetectionStatus.hook";
import {useCallback, useEffect, useState} from "react";
import {lessonPlayerStore} from "../../redux/lesson/LessonPlayerStore";
import {scoringAccuracyTipsModalStore} from "../../redux/lesson/scoringAccuracyTipsModal/scoringAccuracyTipsModalStore";
import {useNavigate} from "react-router-dom";
import {useAppSelector} from "../../redux/hooks";
import lessonService from "../../services/lesson/lessonService";
import {microphone} from "../../hooks/microphone.hook";
import {pause, play, stop} from "../../redux/lesson/lessonActions";
import deviceInfo from "../../services/deviceInfo/deviceInfo";
import {microphoneStore} from "../../redux/microphone/microphoneStore";
import {SegmentModeEnum} from "../../models/view/lessonPage/SegmentModeEnum";
import {settingsStore} from "../../redux/settings/settingsStore";

interface ILessonPageHook {
  isPitchDetectionEnabled: boolean;
  scoreModalProps: {
    isOpen: boolean;
    onClose: () => void;
    onReplay: () => void;
    onNextLessonClick: () => void;
    onNextButtonClick: () => void;
  };
  scoringAccuracyTipsModalProps: {
    isOpen: boolean;
    onClose: () => void;
    isDontShowAgainChecked: boolean;
    setIsDontShowAgainChecked: (isChecked: boolean) => void;
  };
  microphoneAccessModalProps: {
    canBeShown: () => boolean;
    isOpen: boolean;
    close: () => void;
    onRequestMicAccessButton_click: () => void;
  },
  onPlayStopPauseButton_clicked: () => void;
}

/**
 * Responsibility: Be the component-specific hook for the LessonPage.tsx component.
 */
export const useLessonPage = (): ILessonPageHook => {
  /**
   * Important: This hook is called here instead of simply getting what's in Redux, so non-components under
   * the component which uses this hook will be notified when pitch detection transitions to or from being enabled.
   */
  const {isPitchDetectionEnabled} = usePitchDetectionStatus();

  // ScoreModal
  const isScoreModalOpen = useAppSelector(lessonPlayerStore.getDisplayScoreModal);
  const hasEnoughTimeElapsedSinceLastTip = useAppSelector(scoringAccuracyTipsModalStore.getHasUserWaited24HoursBetweenTheLastTimeTheySawThisTipAndTheirLastAudioScore);
  const hasHiddenThisTip = useAppSelector(scoringAccuracyTipsModalStore.getHasUserOptedToHideThisTip);
  const segment = useAppSelector(lessonPlayerStore.getSegment);
  const hasVideo = segment?.hasVideo ?? false;
  const isInteractiveNotation = !hasVideo;
  const areScoreModalActionsDeferred = isPitchDetectionEnabled && isInteractiveNotation && hasEnoughTimeElapsedSinceLastTip && !hasHiddenThisTip;

  useEffect(() => {
    if (isScoreModalOpen) {
      scoringAccuracyTipsModalStore.setLastTimeUserOpenedScoreModalForAudioScoring();
    }
  }, [isScoreModalOpen]);

  // ScoringAccuracyTipsModal
  const [isDontShowAgainChecked, setIsDontShowAgainChecked] = useState<boolean>(false);

  // Both
  const navigate = useNavigate();
  const lesson = useAppSelector(lessonPlayerStore.getLesson);
  const scoreModalViewModel = useAppSelector(lessonPlayerStore.getScoreModalViewModel);
  const [isScoringAccuracyTipsModalOpen, setIsScoringAccuracyTipsModalOpen] = useState<boolean>(false);
  /**
   * These deferred actions are not serializable.
   * This is the key reason why we have a hook, instead of simply using Redux.
   * Alternatives to this approach include:
   * - Put the scoring tips modal on top of the score modal. We wouldn't need to defer any actions.
   * - Tracking the action to defer by using a key in Redux. That is cumbersome, because of the action's arguments.
   */
  const [theActionToPerformAfterScoringAccuracyTipsClose, setTheActionToPerformAfterScoringAccuracyTipsClose] = useState<() => void>(() => () => {});

  const onScoreModalClose = useCallback(() => {
    lessonPlayerStore.setDisplayScoreModal(false);

    if (areScoreModalActionsDeferred) {
      setIsScoringAccuracyTipsModalOpen(true); // Show the modal
    }
  }, [areScoreModalActionsDeferred]);

  const onScoringAccuracyTipsModalClose = useCallback(() => {
    scoringAccuracyTipsModalStore.setLastTimeThisTipWasDisplayed();
    theActionToPerformAfterScoringAccuracyTipsClose();

    if (isDontShowAgainChecked) {
      scoringAccuracyTipsModalStore.setHasUserOptedToHideThisTip();
    }
    setIsScoringAccuracyTipsModalOpen(false);
  }, [isDontShowAgainChecked, theActionToPerformAfterScoringAccuracyTipsClose]);

  const deferAction = useCallback((newFunction: () => void) => {
    /**
     * Warning: This looks weird, but it is the right way to store a function in a state variable.
     * @link https://react.dev/reference/react/useState#im-trying-to-set-state-to-a-function-but-it-gets-called-instead
     * Don't store this function in Redux, since it's not serializable.
     * The input argument shouldn't look like this:
     * - setTheAction(() => newFunction());
     * - setTheAction(newFunction);
     * Instead, it needs to be a function that returns a function, like this:
     * - setTheAction(() => newFunction);
     */
    setTheActionToPerformAfterScoringAccuracyTipsClose(() => newFunction);
  }, [setTheActionToPerformAfterScoringAccuracyTipsClose]);

  const deferOrPerformAction = useCallback((action: () => void) => {
    if (areScoreModalActionsDeferred) {
      deferAction(action);
    } else {
      action();
    }
  }, [deferAction, areScoreModalActionsDeferred]);

  const onReplay = useCallback(() => {
    onScoreModalClose();
    deferOrPerformAction(onPlayStopPauseButton_clicked);
  }, [onScoreModalClose, deferOrPerformAction]);

  const onNextLessonClick = useCallback(() => {
    onScoreModalClose();
    deferOrPerformAction(() => navigate('/lesson/' + lesson?.nextLesson?.id.toString(), {replace: true}));
  }, [onScoreModalClose, deferOrPerformAction, lesson, navigate]);

  const onNextButtonClick = useCallback(() => {
    if (!scoreModalViewModel || !scoreModalViewModel.nextButtonLink) {
      return;
    }
    onScoreModalClose();
    deferOrPerformAction(() => navigate(scoreModalViewModel.nextButtonLink ?? '', {replace: true}));
  }, [scoreModalViewModel, onScoreModalClose, deferOrPerformAction, navigate]);

  // MicrophoneAccessModal
  const [isMicrophoneAccessModalOpen, setIsMicrophoneAccessModalOpen] = useState<boolean>(false);

  // Lesson Player
  function onPlayStopPauseButton_clicked() {
    // Some of this logic is duplicated in playbackOrchestrator.handleNativeAppMessage(). Would be nice to not duplicate it.
    const isPlaying = lessonPlayerStore.getIsPlaying();
    const isCountInPlaying = lessonPlayerStore.getIsCountInPlaying();
    const isPitchDetectionEnabled = lessonPlayerStore.getIsPitchDetectionEnabled();
    const segment = lessonPlayerStore.getSegment();
    const hasVideo = segment?.hasVideo;

    const wasPausePressed = hasVideo && isPlaying || isCountInPlaying;
    const wasStopPressed = !hasVideo && isPlaying || isCountInPlaying;
    const wasPlayPressed = !wasPausePressed && !wasStopPressed;
    const areMicrophonePermissionErrorsShown = wasPlayPressed;

    checkPermissionsIfPitchDetectionIsEnabled(areMicrophonePermissionErrorsShown, isPitchDetectionEnabled)
      .then(() => {
        // Some of this logic is duplicated in playbackOrchestrator.handleNativeAppMessage(). Would be nice to not duplicate it.
        const isPlaying = lessonPlayerStore.getIsPlaying();
        const isCountInPlaying = lessonPlayerStore.getIsCountInPlaying();
        const isPitchDetectionEnabled = lessonPlayerStore.getIsPitchDetectionEnabled();
        const segment = lessonPlayerStore.getSegment();
        const hasVideo = segment?.hasVideo ?? false;

        // Re-check whether play/stop/pause were pressed in case something changed while microphone permissions were being checked.
        // This double-check is more important when the permission check times out, since that takes a half of a second.
        const wasPausePressed = hasVideo && isPlaying || isCountInPlaying;
        const wasStopPressed = !hasVideo && isPlaying || isCountInPlaying;

        // Stop or Paused was pressed
        if(wasStopPressed || wasPausePressed) {
          onStopOrPauseButton_pressed(wasStopPressed, wasPausePressed);

          // Play button was pressed
        } else {
          onPlayButton_pressed(isPitchDetectionEnabled);
        }
      });
  }

  async function checkPermissionsIfPitchDetectionIsEnabled(areMicrophonePermissionErrorsShown: boolean, isPitchDetectionEnabled: boolean) {
    return (isPitchDetectionEnabled)
      ? microphone.checkPermissions(areMicrophonePermissionErrorsShown)
      : Promise.resolve(); // No check is needed. Return a resolved promise.
  }

  function onStopOrPauseButton_pressed(wasStopPressed: boolean, wasPausePressed: boolean): void {
    lessonService.pitchDetector?.stopListening(false);
    if (wasStopPressed) {
      stop();
    } else if (wasPausePressed) {
      pause();
    }
  }

  function onPlayButton_pressed(isPitchDetectionEnabled: boolean): void {
    if (shouldMicrophoneAccessModalBeDisplayed()) {
      setIsMicrophoneAccessModalOpen(true);
    } else {
      startPlayingOrListening(isPitchDetectionEnabled)
    }
  }

  function shouldMicrophoneAccessModalBeDisplayed() {
    const isMidiDeviceConnected = settingsStore.getIsMidiDeviceConnected();
    let isInAVideoOrFollowAlong = false;
    const segment = lessonPlayerStore.getSegment();
    if (segment) {
      isInAVideoOrFollowAlong = segment.mode === SegmentModeEnum.play || segment.mode === SegmentModeEnum.followAlong;
    }

    return deviceInfo.getIsAndroid() && microphoneStore.getPermissionStatus() === "requestable" && !isInAVideoOrFollowAlong && !isMidiDeviceConnected;
  }

  /**
   * Telling the pitchDetector to start listening will cause recording to start which will raise an event and
   * eventually call this.#onRecording_started()
   */
  function startPlayingOrListening(isPitchDetectionEnabled: boolean): void {
    isPitchDetectionEnabled ? lessonService.pitchDetector?.startListening() : play();
  }

  return {
    isPitchDetectionEnabled,
    scoreModalProps: {
      isOpen: isScoreModalOpen,
      onClose: onScoreModalClose,
      onReplay: onReplay,
      onNextLessonClick: onNextLessonClick,
      onNextButtonClick: onNextButtonClick,
    },
    scoringAccuracyTipsModalProps: {
      isOpen: isScoringAccuracyTipsModalOpen,
      onClose: onScoringAccuracyTipsModalClose,
      isDontShowAgainChecked,
      setIsDontShowAgainChecked,
    },
    microphoneAccessModalProps: {
      canBeShown: shouldMicrophoneAccessModalBeDisplayed,
      isOpen: isMicrophoneAccessModalOpen,
      close: () => setIsMicrophoneAccessModalOpen(false),
      onRequestMicAccessButton_click: () => {
        startPlayingOrListening(isPitchDetectionEnabled);
      }
    },
    onPlayStopPauseButton_clicked,
  };
}
